import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import { knowledgeProvidersService } from "@/services/knowledgeProvidersService";
import { newCollectionConfig } from "@/lib/knowledgeProviders";
import type { KnowledgeQdrantCollection, KnowledgeQdrantProvider } from "@/types/knowledgeProviders";
import QdrantCollectionCard from "./QdrantCollectionCard";

interface Props {
  provider: KnowledgeQdrantProvider;
  organizationId?: string;
  keysReady: boolean;
  allToolNames: string[];
  onChange: (next: KnowledgeQdrantProvider) => void;
}

export default function QdrantProviderCard({ provider, organizationId, keysReady, allToolNames, onChange }: Props) {
  const [available, setAvailable] = useState<string[] | null>(null);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);

  const fail = (title: string, e: unknown) =>
    toast({ title, description: e instanceof Error ? e.message : String(e), variant: "destructive" });

  const loadCollections = async () => {
    if (!organizationId) return;
    setBusy(true);
    try {
      setAvailable(await knowledgeProvidersService.listCollections(organizationId));
    } catch (e) {
      fail("Could not list collections", e);
    } finally {
      setBusy(false);
    }
  };

  const addCollection = async () => {
    if (!organizationId || !picked) return;
    setBusy(true);
    try {
      const { schema, description } = await knowledgeProvidersService.introspect(organizationId, picked);
      const collection = newCollectionConfig(picked, schema, description, allToolNames);
      onChange({ ...provider, collections: [...provider.collections, collection] });
      setPicked("");
    } catch (e) {
      fail("Could not read the collection", e);
    } finally {
      setBusy(false);
    }
  };

  const replace = (id: string, next: KnowledgeQdrantCollection | null) =>
    onChange({
      ...provider,
      collections: next
        ? provider.collections.map((c) => (c.id === id ? next : c))
        : provider.collections.filter((c) => c.id !== id),
    });

  return (
    <div className="space-y-4">
      {provider.collections.map((c) => (
        <QdrantCollectionCard
          key={c.id}
          collection={c}
          organizationId={organizationId}
          // Names of the other collections' tools, so this one's name can be checked for clashes.
          otherToolNames={allToolNames.filter((n, i) => allToolNames.indexOf(n) === i && n !== c.tool_name)}
          onChange={(next) => replace(c.id, next)}
        />
      ))}

      <div className="space-y-2">
        <Label>Add a collection</Label>
        <div className="flex gap-2">
          {available === null ? (
            <Button type="button" variant="outline" size="sm" disabled={!keysReady || busy} onClick={loadCollections}>
              {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Load collections
            </Button>
          ) : (
            <>
              <Select value={picked} onValueChange={setPicked}>
                <SelectTrigger className="w-72">
                  <SelectValue placeholder={available.length ? "Pick a collection" : "No collections found"} />
                </SelectTrigger>
                <SelectContent>
                  {available.map((name) => (
                    <SelectItem key={name} value={name}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" size="sm" disabled={!picked || busy} onClick={addCollection}>
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Add
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
